import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import comImg   from "../../../../assets/images/domain/com.png";
import comAUImg from "../../../../assets/images/domain/com_au.svg";
import netAUImg from "../../../../assets/images/domain/net_au.svg";

const tlds = [
    { ext: ".com",    newPrice: "0.05", tag: "Most Popular", img: comImg   },
    { ext: ".com.au", newPrice: "0.01", tag: "Australian",   img: comAUImg },
    { ext: ".net.au", newPrice: "0.01", tag: "Network",      img: netAUImg },
];

function useCountUp(target: string, duration: number, start: boolean) {
    const [val, setVal] = useState("0.00");
    useEffect(() => {
        if (!start) return;
        let frame: number;
        const startTime = performance.now();
        const num = parseFloat(target);
        const tick = (now: number) => {
            const p = Math.min((now - startTime) / duration, 1);
            const ease = 1 - Math.pow(1 - p, 3);
            setVal((ease * num).toFixed(2));
            if (p < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [start, target, duration]);
    return val;
}

function OfferCard({ tld, index, inView }: { tld: typeof tlds[0]; index: number; inView: boolean }) {
    const [hovered, setHovered] = useState(false);
    const price = useCountUp(tld.newPrice, 900 + index * 200, inView);

    return (
        <div
            className="h-full transition-all duration-700 ease-out"
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(30px)",
                transitionDelay: `${index * 120}ms`,
            }}
        >
            <div
                className="relative flex flex-col items-center text-center gap-4 rounded-2xl px-6 py-8 cursor-pointer transition-all duration-300 overflow-hidden h-full"
                style={{
                    background: hovered
                        ? "linear-gradient(135deg, rgba(109,40,217,0.26) 0%, rgba(37,99,235,0.20) 100%)"
                        : "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.07) 100%)",
                    border: hovered
                        ? "1px solid rgba(139,92,246,0.45)"
                        : "1px solid rgba(255,255,255,0.13)",
                    backdropFilter: "blur(12px)",
                    boxShadow: hovered
                        ? "0 16px 40px rgba(109,40,217,0.25), inset 0 1px 0 rgba(255,255,255,0.10)"
                        : "0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)",
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {/* Top accent bar */}
                <div
                    className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl transition-all duration-300"
                    style={{
                        background: "linear-gradient(to right, #7c3aed, #2563eb)",
                        opacity: hovered ? 1 : 0.35,
                    }}
                />

                {/* Glow blob on hover */}
                <div
                    className="absolute inset-0 pointer-events-none transition-opacity duration-300 rounded-2xl"
                    style={{
                        opacity: hovered ? 1 : 0,
                        background: "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.12) 0%, transparent 70%)",
                    }}
                />

                {/* Domain image */}
                <div
                    className="mt-2 flex items-center justify-center transition-all duration-300"
                    style={{
                        filter: hovered
                            ? "drop-shadow(0 4px 12px rgba(139,92,246,0.50)) brightness(1.1)"
                            : "drop-shadow(0 2px 4px rgba(0,0,0,0.40)) brightness(0.9)",
                        transform: hovered ? "scale(1.08)" : "scale(1)",
                    }}
                >
                    <img
                        src={tld.img}
                        alt={tld.ext}
                        className="h-10 w-auto object-contain"
                    />
                </div>

                {/* Tag badge */}
                <span
                    className="text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full transition-all duration-300"
                    style={{
                        background: hovered ? "rgba(139,92,246,0.18)" : "rgba(255,255,255,0.06)",
                        color: hovered ? "#c4b5fd" : "#6b7280",
                        border: hovered ? "1px solid rgba(139,92,246,0.35)" : "1px solid rgba(255,255,255,0.1)",
                    }}
                >
                    {tld.tag}
                </span>

                {/* Divider */}
                <div className="w-10 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />

                {/* Price */}
                <div>
                    <div className="text-sm uppercase tracking-wider font-semibold mb-1 text-gray-300">
                        1st year
                    </div>
                    <div
                        className="font-bold text-4xl leading-none"
                        style={{
                            color: hovered ? "#c4b5fd" : "#f9fafb",
                            transition: "color 0.3s",
                        }}
                    >
                        ${price}
                        <span className="text-sm font-semibold ml-1" style={{ color: "#6b7280" }}>/yr*</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function DomainPromo() {
    const [inView, setInView] = useState(false);
    const ref = useRef<HTMLElement>(null);

    // ✅ useNavigate is now correctly inside the component
    const navigate = useNavigate();

    // ✅ goToSearch is now correctly inside the component
    const goToSearch = () => {
        navigate("/domains/search");
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setInView(true); },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <style>{`
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeLeft {
                    from { opacity: 0; transform: translateX(30px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes floatImg {
                    0%, 100% { transform: translateY(0px); }
                    50%      { transform: translateY(-12px); }
                }
                @keyframes gradientShift {
                    0%, 100% { background-position: 0% 50%; }
                    50%      { background-position: 100% 50%; }
                }
            `}</style>

            <section
                ref={ref}
                className="relative w-full overflow-hidden pt-24 md:pt-32 pb-20 px-4 sm:px-6"
            >
                {/* Dark bg blobs */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div
                        className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-[160px]"
                        style={{ background: "rgba(109,40,217,0.08)" }}
                    />
                    <div
                        className="absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full blur-[140px]"
                        style={{ background: "rgba(37,99,235,0.07)" }}
                    />
                    <div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full blur-[180px]"
                        style={{ background: "rgba(139,92,246,0.04)" }}
                    />
                </div>

                {/* Subtle dot grid */}
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        opacity: 0.025,
                        backgroundImage: "radial-gradient(circle, rgba(139,92,246,1) 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                    }}
                />

                <div className="relative z-10 max-w-6xl mx-auto">

                    {/* ── TOP: headline + image ── */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16 mb-14">

                        {/* Left: copy */}
                        <div
                            className="flex-1"
                            style={inView ? { animation: "fadeUp 0.6s ease both" } : { opacity: 0 }}
                        >
                            <h2 className="mb-5 text-4xl md:text-5xl lg:text-6xl font-bold">
                                <span className="mb-3 block">Get a .com for only</span>
                                <span
                                    style={{
                                        color: "transparent",
                                        WebkitTextFillColor: "transparent",
                                        backgroundImage: "linear-gradient(100deg, #a78bfa 0%, #60a5fa 100%)",
                                        WebkitBackgroundClip: "text",
                                        backgroundClip: "text",
                                        display: "inline",
                                    }}
                                >
                                    $0.05/1st yr
                                </span>
                            </h2>

                            <p className="text-lg font-light leading-relaxed max-w-lg text-gray-300">
                                *3-year purchase required.
                            </p>

                            <div
                                className="flex flex-col items-start gap-3 mt-6"
                                style={{ opacity: inView ? 1 : 0, transition: "opacity 0.7s ease 0.5s" }}
                            >
                                <button
                                    onClick={goToSearch}
                                    className="flex items-center gap-2.5 px-8 py-3.5 rounded-xl font-semibold text-white transition-all duration-200 hover:-translate-y-px"
                                    style={{
                                        background: "linear-gradient(135deg, #7c3aed, #2563eb)",
                                        boxShadow: "0 8px 28px rgba(124,58,237,0.45), 0 0 0 1px rgba(139,92,246,0.2)",
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.boxShadow = "0 12px 36px rgba(124,58,237,0.6), 0 0 0 1px rgba(139,92,246,0.3)";
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.boxShadow = "0 8px 28px rgba(124,58,237,0.45), 0 0 0 1px rgba(139,92,246,0.2)";
                                    }}
                                >
                                    Register Now
                                </button>
                            </div>
                        </div>

                        {/* Right: image */}
                        <div
                            className="relative lg:w-[440px] xl:w-[480px] shrink-0 flex justify-center"
                            style={inView ? { animation: "fadeLeft 0.7s 0.2s ease both" } : { opacity: 0 }}
                        >
                            {/* Corner accents */}
                            <div
                                className="absolute -top-3 -left-3 w-16 h-16 rounded-2xl"
                                style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.5), rgba(37,99,235,0.3))", opacity: 0.4 }}
                            />
                            <div
                                className="absolute -bottom-3 -right-3 w-10 h-10 rounded-xl"
                                style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.4), rgba(139,92,246,0.3))", opacity: 0.3 }}
                            />

                            <div
                                className="relative w-full h-[420px] rounded-[2rem] overflow-hidden"
                                style={{
                                    boxShadow: "0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(139,92,246,0.15), 0 8px 24px rgba(109,40,217,0.2)",
                                    animation: "floatImg 5s ease-in-out infinite",
                                }}
                            >
                                <img
                                    src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80"
                                    alt="Developer working on domain"
                                    className="absolute inset-0 w-full h-full object-cover object-center"
                                    style={{ filter: "brightness(0.75) saturate(0.9)" }}
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{ background: "rgba(0,0,0,0.25)" }}
                                />
                                <div
                                    className="absolute bottom-0 left-0 right-0 h-36"
                                    style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75), transparent)" }}
                                />
                            </div>

                            {/* Price badge */}
                            <div
                                className="absolute -top-4 -right-4 flex flex-col items-center justify-center w-24 h-24 rounded-2xl rotate-6"
                                style={{
                                    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
                                    boxShadow: "0 8px 24px rgba(124,58,237,0.55), 0 0 0 1px rgba(139,92,246,0.3)",
                                }}
                            >
                                <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>from</span>
                                <span className="py-1" style={{ color: "#fff", fontWeight: 900, fontSize: "22px", lineHeight: 1 }}>$0.05</span>
                                <span style={{ color: "rgba(255,255,255,0.65)", fontSize: "14px" }}>/yr*</span>
                            </div>
                        </div>
                    </div>

                    {/* ── 3-column offer cards ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        {tlds.map((tld, i) => (
                            <OfferCard key={tld.ext} tld={tld} index={i} inView={inView} />
                        ))}
                    </div>

                </div>
            </section>
        </>
    );
}